import { Drawer, message, Select } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import {
    createFinancialYear,
    updateFinancialYear,
    fetchFinancialYearsByCompanyId,
} from "../../../core/data/redux/financialYear/financialYearActions";
import dayjs from "dayjs";

const FinancialYearModal: React.FC<any> = ({
    modalType,
    isModalVisible,
    handleCancel,
    selectedRowData,
    setTableKey,
    companyId
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loading } = useSelector((state: any) => state.financialYears);
    
    const [formData, setFormData] = useState({
        name: "",
        start_date: "",
        end_date: "",
        CompanyID: companyId || 1,
    });

    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        if (isModalVisible) {
            if (modalType === "edit" && selectedRowData) {

                setFormData({
                    name: selectedRowData.name || "",
                    start_date: selectedRowData.start_date ? dayjs(selectedRowData.start_date).format("YYYY-MM-DD") : "",
                    end_date: selectedRowData.end_date ? dayjs(selectedRowData.end_date).format("YYYY-MM-DD") : "",
                    CompanyID: selectedRowData.CompanyID || companyId || user?.CompanyID || 1,
                });
            } else if (modalType === "create") {
                setFormData({
                    name: "",
                    start_date: "",
                    end_date: "",
                    CompanyID: companyId || user?.CompanyID || 1,
                });
            }
            setErrors({});
        }
    }, [isModalVisible, modalType, selectedRowData, user?.CompanyID, companyId]);


    const handleInputChange = (e: any, field: string) => {
        const { value } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateForm = () => {
        let valid = true;
        let newErrors: any = {};

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
            valid = false;
        } else if (!/^fy_\d{4}$/.test(formData.name.trim())) {
            newErrors.name = "Name must be in the format fy_2025";
            valid = false;
        }
        if (!formData.start_date) {
            newErrors.start_date = "Start date is required";
            valid = false;
        }
        if (!formData.end_date) {
            newErrors.end_date = "End date is required";
            valid = false;
        }
        if (formData.start_date && formData.end_date) {
            const start = new Date(formData.start_date);
            const end = new Date(formData.end_date);
            if (start > end) {
                newErrors.start_date = "Start date cannot be after end date";
                valid = false;
            }
        }
        setErrors(newErrors);
        return valid;
    };

    const resetForm = () => {
        setFormData({
            name: "",
            start_date: "",
            end_date: "",
            CompanyID: companyId || user?.CompanyID || 1,
        });
        setErrors({});
    };


    const handleSubmit = async () => {
        if (!validateForm()) return;

        // Convert dates before sending
        const formattedFormData = {
            ...formData,
            start_date: dayjs(formData.start_date).format("YYYY-MM-DD"),
            end_date: dayjs(formData.end_date).format("YYYY-MM-DD"),
        };

        if (modalType === "create") {
            const result = await dispatch(createFinancialYear(formattedFormData));
            if (result.meta.requestStatus === "fulfilled") {
                message.success("Financial year created successfully");
                resetForm();
                if (companyId) {
                    dispatch(fetchFinancialYearsByCompanyId(companyId));
                }
                setTableKey?.((prev: any) => prev + 1);
                handleCancel();
            } else {
                message.error(result.payload.error);
            }
        } else if (modalType === "edit" && selectedRowData?.id) {
            const result = await dispatch(
                updateFinancialYear({ id: selectedRowData?.id, ...formattedFormData })
            );
            if (result.meta.requestStatus === "fulfilled") {
                message.success("Financial year updated successfully");
                if (companyId) {
                    dispatch(fetchFinancialYearsByCompanyId(companyId));
                }
                setTableKey?.((prev: any) => prev + 1);
                resetForm();
                handleCancel();
            } else {
                message.error(result.payload.error);
            }
        }
    };

    return (
        <Drawer
            title={modalType === "edit" ? "Edit Financial Year" : "Add Financial Year"}
            placement="right"
            width={600}
            onClose={() => {
                resetForm();
                handleCancel();
            }}
            visible={isModalVisible}
            style={{ marginTop: 50 }}
        >
            <form>
                <div className="row">
                    <div className="col-md-12 mb-3">
                        <label className="form-label">Name <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            className="form-control"
                            value={formData.name}
                            onChange={(e) => handleInputChange(e, "name")}
                        />
                        {errors.name && <span className="text-danger">{errors.name}</span>}
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Start Date <span className="text-danger">*</span></label>
                        <input
                            type="date"
                            className="form-control"
                            placeholder="DD-MM-YYYY"
                            value={formData.start_date}
                            onChange={(e) => handleInputChange(e, "start_date")}
                        />
                        {errors.start_date && <span className="text-danger">{errors.start_date}</span>}
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">End Date <span className="text-danger">*</span></label>
                        <input
                            type="date"
                            className="form-control"
                            placeholder="DD-MM-YYYY"
                            value={formData.end_date}
                            onChange={(e) => handleInputChange(e, "end_date")}
                        />
                        {errors.end_date && <span className="text-danger">{errors.end_date}</span>}
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-light me-2"
                        onClick={() => {
                            resetForm();
                            handleCancel();
                        }}
                    >
                        Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                        {loading && <span className="spinner-border spinner-border-sm me-2" />}
                        {modalType === "edit" ? "Update" : "Create"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default FinancialYearModal;
